import React from 'react';
import { useParams } from 'react-router';
import {
  Notification,
  Checkbox,
  SelectMini,
  Tab,
  TabBar,
  Tabs,
  TabView,
  NotificationVariation,
  Gap,
  GridLayout,
} from '@salecycle/ui-component-library';
import { useSite } from 'sections/hooks/useSite';
import { useFeatureFlags } from 'core/app/AppContext';
import { NotFoundPage } from 'core/ui/components/error';
import { useTranslation } from 'react-i18next';
import { Site } from 'sections/client-management/entities';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';
import { useMutation } from 'react-query';
import { getSendRulesUpdatedSite } from '../utils/getSendRulesUpdatedSite';
import { LegacyCard } from 'components/experimental/Card/LegacyCard';
import { StandardMainArea } from 'components/experimental/StandardMainArea';

export function SendRulesPage() {
  const { t } = useTranslation('compliance');
  const { flagEnabled } = useFeatureFlags();
  const { siteId } = useParams<{ siteId: string }>();
  const { data: site, isLoading, isError } = useSite(siteId);

  const [notificationIsOpen, setNotificationIsOpen] = React.useState(false);
  const [notificationText, setNotificationText] = React.useState('');
  const [notificationVariation, setNotificationVariation] = React.useState<NotificationVariation>('normal');

  const [sendRulesEnabled, setSendRulesEnabled] = React.useState(false);
  const [selectedBrowseOption, setSelectedBrowseOption] = React.useState(null);
  const [selectedCartOption, setSelectedCartOption] = React.useState(null);
  const [selectedBulkRetargetingOption, setSelectedBulkRetargetingOption] = React.useState(null);

  const consentOptions = React.useMemo(
    () => [
      { label: t('sendRulesPage.consentStatusOptions.ifNotOptedOut'), value: 'NOT_OPTED_OUT' },
      { label: t('sendRulesPage.consentStatusOptions.ifOptedIn'), value: 'OPTED_IN' },
    ],
    [t],
  );

  const [initialState, setInitialState] = React.useState<null | {
    sendRulesEnabled: boolean;
    browse: string;
    cart: string;
    bulk: string;
  }>(null);

  const updateFormStateFromSite = React.useCallback(() => {
    const rules = site?.sendRules?.rules || [];

    const getConsent = (product: string) => rules.find(r => r.product === product)?.consentStatus ?? 'NOT_OPTED_OUT';

    const currentInitialState = {
      sendRulesEnabled: rules.length > 0,
      browse: getConsent('BROWSE'),
      cart: getConsent('CART'),
      bulk: getConsent('BULK_RETARGETING'),
    };

    setSendRulesEnabled(currentInitialState.sendRulesEnabled);
    setSelectedBrowseOption(consentOptions.find(opt => opt.value === currentInitialState.browse));
    setSelectedCartOption(consentOptions.find(opt => opt.value === currentInitialState.cart));
    setSelectedBulkRetargetingOption(consentOptions.find(opt => opt.value === currentInitialState.bulk));
    setInitialState(currentInitialState);
  }, [site, consentOptions]);

  React.useEffect(() => {
    if (site) {
      updateFormStateFromSite();
    }
  }, [site, updateFormStateFromSite]);

  const hasUnsavedChanges =
    initialState &&
    (sendRulesEnabled !== initialState.sendRulesEnabled ||
      selectedBrowseOption?.value !== initialState.browse ||
      selectedCartOption?.value !== initialState.cart ||
      selectedBulkRetargetingOption?.value !== initialState.bulk);

  const showNotification = (text: string, variation: NotificationVariation) => {
    setNotificationText(text);
    setNotificationVariation(variation);
    setNotificationIsOpen(true);
  };

  const putSiteMutation = useMutation((site: Site) => RestApi.put(`/sites/${siteId}`, site));

  const handleSaveButtonClicked = () => {
    const updatedSite = getSendRulesUpdatedSite(site, sendRulesEnabled, {
      rules: [
        { product: 'BROWSE', consentStatus: selectedBrowseOption?.value },
        { product: 'CART', consentStatus: selectedCartOption?.value },
        { product: 'BULK_RETARGETING', consentStatus: selectedBulkRetargetingOption?.value },
      ],
    });

    putSiteMutation.mutate(updatedSite, {
      onSuccess: () => {
        showNotification(t('sendRulesPage.notifications.savedSuccessfully'), 'normal');
        setInitialState({
          sendRulesEnabled,
          browse: selectedBrowseOption?.value,
          cart: selectedCartOption?.value,
          bulk: selectedBulkRetargetingOption?.value,
        });
      },
      onError: (error: RestApiError) => {
        showNotification(`${t('sendRulesPage.notifications.errorSaving')}: ${error.message}`, 'error');
      },
    });
  };

  const handleDiscardButtonClicked = () => {
    updateFormStateFromSite();
    showNotification(t('sendRulesPage.notifications.allChangesDiscarded'), 'normal');
  };

  if (!flagEnabled('compliance-send-rules')) return <NotFoundPage />;

  return (
    <>
      <Notification
        variation={notificationVariation}
        isOpen={notificationIsOpen}
        onChange={() => setNotificationIsOpen(false)}
        delay={3000}
      >
        {notificationText}
      </Notification>

      <StandardMainArea
        heading={t('sendRulesPage.heading')}
        subHeading={t('sendRulesPage.subHeading')}
        isLoading={isLoading}
        isError={isError}
        errorHeading={t('sendRulesPage.errorHeading')}
        saveBarEnabled={true}
        saveButtonDisabled={!hasUnsavedChanges || isLoading}
        onSaveButtonClicked={handleSaveButtonClicked}
        onDiscardButtonClicked={handleDiscardButtonClicked}
      >
        <Checkbox
          id="enabled-check"
          label={t('sendRulesPage.enableSendRulesCheckbox')}
          disabled={isLoading}
          checked={sendRulesEnabled}
          onChange={setSendRulesEnabled}
        />

        <Gap top={30} />

        {sendRulesEnabled && (
          <Tabs>
            <TabBar>
              <Tab tabId="browse">{t('sendRulesPage.tabs.browse')}</Tab>
              <Tab tabId="cart">{t('sendRulesPage.tabs.cart')}</Tab>
              <Tab tabId="bulkRetargeting">{t('sendRulesPage.tabs.bulkRetargeting')}</Tab>
            </TabBar>

            <TabView tabId="browse">
              <GridLayout childWidth={400} contentPadding={0}>
                <LegacyCard heading="Email" variation="primary">
                  <LegacyCard>
                    <span>
                      {t('sendRulesPage.sendRules.browse_email')}{' '}
                      <SelectMini
                        ariaLabel={t('sendRulesPage.sendRules.browseDropdownAria')}
                        options={consentOptions}
                        value={selectedBrowseOption}
                        onChange={setSelectedBrowseOption}
                      />
                    </span>
                  </LegacyCard>
                </LegacyCard>
                {flagEnabled('compliance-sms-send-rules') && (
                  <LegacyCard heading="SMS" variation="primary" contentTextAlign="center">
                    <LegacyCard>Coming Soon!</LegacyCard>
                  </LegacyCard>
                )}
              </GridLayout>
            </TabView>

            <TabView tabId="cart">
              <GridLayout childWidth={400} contentPadding={0}>
                <LegacyCard heading="Email" variation="primary">
                  <LegacyCard>
                    <span>
                      {t('sendRulesPage.sendRules.cart_email')}{' '}
                      <SelectMini
                        ariaLabel={t('sendRulesPage.sendRules.cartDropdownAria')}
                        options={consentOptions}
                        value={selectedCartOption}
                        onChange={setSelectedCartOption}
                      />
                    </span>
                  </LegacyCard>
                </LegacyCard>
                {flagEnabled('compliance-sms-send-rules') && (
                  <LegacyCard heading="SMS" variation="primary" contentTextAlign="center">
                    <LegacyCard>Coming Soon!</LegacyCard>
                  </LegacyCard>
                )}
              </GridLayout>
            </TabView>

            <TabView tabId="bulkRetargeting">
              <GridLayout childWidth={400} contentPadding={0}>
                <LegacyCard heading="Email" variation="primary">
                  <LegacyCard>
                    <span>
                      {t('sendRulesPage.sendRules.bulkRetargeting_email')}{' '}
                      <SelectMini
                        ariaLabel={t('sendRulesPage.sendRules.bulkRetargetingDropdownAria')}
                        options={consentOptions}
                        value={selectedBulkRetargetingOption}
                        onChange={setSelectedBulkRetargetingOption}
                      />
                    </span>
                  </LegacyCard>
                </LegacyCard>
                {flagEnabled('compliance-sms-send-rules') && (
                  <LegacyCard heading="SMS" variation="primary" contentTextAlign="center">
                    <LegacyCard>Coming Soon!</LegacyCard>
                  </LegacyCard>
                )}
              </GridLayout>
            </TabView>
          </Tabs>
        )}
      </StandardMainArea>
    </>
  );
}
